import type { FieldOptions } from '@form-validation/core'
import { Core, formValidation } from '@form-validation/bundle/full'
import { Bootstrap5 } from '@form-validation/plugin-bootstrap5'
import { Excluded } from '@form-validation/plugin-excluded'
import type { MessagePlacedEvent } from '@form-validation/plugin-message'
import { Trigger } from '@form-validation/plugin-trigger'

class CrmBootstrap5 extends Bootstrap5 {
  prepareElementContainer(field: string, element: HTMLElement): void {
    const opts = (this as unknown as { opts: { rowSelector: string | ((field: string, element: HTMLElement) => string); rowClasses?: string } }).opts
    const selector = typeof opts.rowSelector === 'string' ? opts.rowSelector : opts.rowSelector(field, element)
    const groupEle = element.closest(selector)

    if (groupEle && groupEle !== element) {
      const classes = [opts.rowClasses, 'fv-plugins-icon-container'].filter((cls): cls is string => typeof cls === 'string' && cls.trim().length > 0)
      if (classes.length > 0) groupEle.classList.add(...classes)
      ;(this as unknown as { containers: Map<HTMLElement, Element> }).containers.set(element, groupEle)
    }
  }

  protected onMessagePlaced(event: MessagePlacedEvent): void {
    super.onMessagePlaced(event)
    const type = event.element.getAttribute('type')
    if (type !== 'checkbox' && type !== 'radio') return
    const choice = event.element.closest('[data-choice]')
    if (choice) choice.appendChild(event.messageElement)
  }
}

export function createFormValidation(formElement: HTMLFormElement, config: {
  fields: Record<string, FieldOptions>
  excluded?: (field: string, element: HTMLElement, elements: HTMLElement[]) => boolean
  validationEvents?: Record<string, string | false>
  submitOnly?: boolean
}): Core {
  return formValidation(formElement, {
    fields: config.fields,
    plugins: {
      bootstrap5: new CrmBootstrap5({
        formClass: 'fv-plugins-bootstrap5',
        rowInvalidClass: 'fv-plugins-bootstrap5-row-invalid',
        rowPattern: /^(.*)(col|offset)(-(sm|md|lg|xl))*-[0-9]+(.*)$/,
        rowSelector: '.fv-row, .form-group, .mb-2, .mb-3, .mb-4, .col, [class*="col-"]',
        rowValidClass: 'fv-plugins-bootstrap5-row-valid',
      }),
      ...(config.submitOnly ? {} : { trigger: new Trigger({ event: config.validationEvents ?? 'input blur change' }) }),
      excluded: new Excluded(config.excluded ? { excluded: config.excluded } : undefined),
    },
  })
}

export async function isFormValid(fvinst: Core | null | undefined): Promise<boolean> {
  if (!fvinst) return false
  const result = await fvinst.validate()
  return result === 'Valid'
}
